import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useSearchParams } from "react-router-dom";
import { ArrowRight, ArrowLeft, CheckCircle, PaperPlaneRight } from "@phosphor-icons/react";
import { fetchServices, fetchStates, submitLead } from "@/lib/api";
import NorthAmericaMap from "@/components/NorthAmericaMap";
import PageHero from "@/components/PageHero";
import { toast } from "sonner";

const STEPS = ["Project", "Timeline", "Contact"];

export default function GetStarted() {
    const [step, setStep] = useState(0);
    const [services, setServices] = useState([]);
    const [states, setStates] = useState([]);
    const [form, setForm] = useState({
        service_type: "",
        project_type: "",
        acreage: "",
        state: "",
        timeline: "",
        budget: "",
        name: "",
        email: "",
        phone: "",
        company: "",
        message: "",
    });
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);
    const [searchParams] = useSearchParams();

    useEffect(() => {
        document.title = "Surveys2CAD | Get Started";
        fetchServices().then((s) => {
            setServices(s);
            if (s.length) setForm((f) => ({ ...f, service_type: s[0].name }));
        });
        fetchStates().then(setStates);

        // Pre-fill state if coming from the map detail panel. Keep the user
        // on step 0 (Project) so they can see the pre-filled state before
        // advancing to timeline/contact details.
        const preState = searchParams.get("state");
        if (preState) {
            setForm((f) => ({ ...f, state: preState }));
        }
    }, [searchParams]); // searchParams is stable from react-router.

    const next = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
    const prev = () => setStep((s) => Math.max(s - 1, 0));

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await submitLead({
                ...form,
                acreage: form.acreage ? parseFloat(form.acreage) : null,
                source: "get_started",
            });
            setDone(true);
            toast.success("Project submitted! We'll reach out within 1 business hour.");
        } catch {
            toast.error("Something went wrong. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    if (done) {
        return (
            <div className="max-w-3xl mx-auto px-5 py-24 text-center" data-testid="get-started-success">
                <CheckCircle size={56} weight="fill" className="text-cyan-400 mx-auto mb-6" />
                <h1 className="font-heading text-4xl md:text-5xl font-bold text-slate-50 tracking-tighter">
                    Project received.
                </h1>
                <p className="text-slate-400 mt-5 text-lg leading-relaxed">
                    A senior drafter is reviewing your requirements. Expect a detailed quote and turnaround estimate in
                    your inbox within one business hour.
                </p>
                <a href="/" className="btn-ghost mt-8 inline-flex">
                    <ArrowLeft size={16} /> Back to home
                </a>
            </div>
        );
    }

    return (
        <div>
            <PageHero variant="get-started" tint="amber">
                <div className="max-w-7xl mx-auto px-5 lg:px-8 pt-16 pb-10 md:pt-24">
                    <div className="max-w-3xl">
                        <div className="overline mb-3">Get started</div>
                        <h1 className="font-heading text-5xl sm:text-6xl font-bold text-slate-50 tracking-tighter leading-[1.05]">
                            Three steps to a <br />
                            <span
                                className="bg-clip-text text-transparent"
                                style={{ backgroundImage: "linear-gradient(120deg, #fb923c 0%, #22d3ee 60%, #a855f7 100%)" }}
                            >
                                detailed quote.
                            </span>
                        </h1>
                        <p className="text-slate-400 mt-6 text-lg">
                            Tell us about your project — we'll respond within 1 business hour with a fixed-price quote and turnaround.
                        </p>
                    </div>
                </div>
            </PageHero>

            <div className="max-w-7xl mx-auto px-5 lg:px-8 py-12 md:py-16">

            {/* Stepper */}
            <div className="flex items-center gap-3 mt-10 mb-8" data-testid="form-stepper">
                {STEPS.map((s, i) => (
                    <div key={s} className="flex items-center gap-3 flex-1">
                        <div
                            className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-mono-alt font-bold transition-all ${
                                i <= step
                                    ? "bg-cyan-500 text-slate-900"
                                    : "bg-slate-800 text-slate-500 border border-slate-700"
                            }`}
                        >
                            {i + 1}
                        </div>
                        <div className={`text-sm font-heading ${i <= step ? "text-slate-100" : "text-slate-500"}`}>{s}</div>
                        {i < STEPS.length - 1 && (
                            <div className={`flex-1 h-px ${i < step ? "bg-cyan-500" : "bg-slate-800"}`} />
                        )}
                    </div>
                ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
                <form onSubmit={submit} className="lg:col-span-3 tech-card p-6 md:p-8" data-testid="get-started-form">
                    <motion.div key={step} initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }}>
                        {step === 0 && (
                            <div className="space-y-4">
                                <h3 className="font-heading text-xl font-semibold text-slate-50 mb-2">Project details</h3>
                                <SelectField
                                    label="Service type" required
                                    value={form.service_type}
                                    onChange={(v) => setForm({ ...form, service_type: v })}
                                    options={services.map((s) => ({ label: s.name, value: s.name }))}
                                    testid="gs-service"
                                />
                                <InputField
                                    label="Project / project name" value={form.project_type}
                                    onChange={(v) => setForm({ ...form, project_type: v })}
                                    placeholder="e.g., Maple Creek Subdivision — Phase 2"
                                    testid="gs-project"
                                />
                                <div className="grid grid-cols-2 gap-3">
                                    <InputField
                                        label="Acreage" type="number" value={form.acreage}
                                        onChange={(v) => setForm({ ...form, acreage: v })}
                                        placeholder="e.g., 42"
                                        testid="gs-acreage"
                                    />
                                    <SelectField
                                        label="State" value={form.state}
                                        onChange={(v) => setForm({ ...form, state: v })}
                                        options={[{ label: "Select state", value: "" }, ...states.map((s) => ({ label: s, value: s }))]}
                                        testid="gs-state"
                                    />
                                </div>
                            </div>
                        )}
                        {step === 1 && (
                            <div className="space-y-4">
                                <h3 className="font-heading text-xl font-semibold text-slate-50 mb-2">Timeline & budget</h3>
                                <SelectField
                                    label="When do you need it?" required
                                    value={form.timeline}
                                    onChange={(v) => setForm({ ...form, timeline: v })}
                                    options={[
                                        { label: "Select timeline", value: "" },
                                        { label: "Rush (24 hrs)", value: "rush-24" },
                                        { label: "48 hours", value: "48h" },
                                        { label: "3–5 business days", value: "3-5d" },
                                        { label: "1–2 weeks", value: "1-2w" },
                                        { label: "Flexible", value: "flex" },
                                    ]}
                                    testid="gs-timeline"
                                />
                                <SelectField
                                    label="Budget range" value={form.budget}
                                    onChange={(v) => setForm({ ...form, budget: v })}
                                    options={[
                                        { label: "Select budget", value: "" },
                                        { label: "< $1,000", value: "lt-1k" },
                                        { label: "$1,000 – $5,000", value: "1k-5k" },
                                        { label: "$5,000 – $25,000", value: "5k-25k" },
                                        { label: "$25,000+", value: "25k+" },
                                        { label: "Need recommendation", value: "recommend" },
                                    ]}
                                    testid="gs-budget"
                                />
                                <div>
                                    <label className="overline mb-2 block">Additional notes</label>
                                    <textarea
                                        rows={4}
                                        value={form.message}
                                        onChange={(e) => setForm({ ...form, message: e.target.value })}
                                        className="w-full bg-slate-900/70 border border-slate-700 rounded-lg px-3 py-2.5 text-slate-100 focus:outline-none focus:border-cyan-500 resize-none"
                                        placeholder="Special deliverable requirements, CAD standards, etc."
                                        data-testid="gs-notes"
                                    />
                                </div>
                            </div>
                        )}
                        {step === 2 && (
                            <div className="space-y-4">
                                <h3 className="font-heading text-xl font-semibold text-slate-50 mb-2">Contact details</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    <InputField label="Full name" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="gs-name" />
                                    <InputField label="Work email" type="email" required value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="gs-email" />
                                    <InputField label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="gs-phone" />
                                    <InputField label="Company" value={form.company} onChange={(v) => setForm({ ...form, company: v })} testid="gs-company" />
                                </div>
                                <div className="tech-card p-4 bg-cyan-500/5 border-cyan-500/30">
                                    <div className="text-sm text-slate-200">
                                        <strong className="text-cyan-300">Summary:</strong> {form.service_type || "—"}
                                        {form.acreage ? ` · ${form.acreage} ac` : ""}
                                        {form.state ? ` · ${form.state}` : ""}
                                        {form.timeline ? ` · ${form.timeline}` : ""}
                                    </div>
                                </div>
                            </div>
                        )}
                    </motion.div>

                    <div className="flex items-center justify-between mt-8 pt-6 border-t border-slate-800">
                        <button
                            type="button"
                            onClick={prev}
                            disabled={step === 0}
                            className="btn-ghost disabled:opacity-40 disabled:cursor-not-allowed"
                            data-testid="gs-prev-btn"
                        >
                            <ArrowLeft size={16} /> Back
                        </button>
                        {step < STEPS.length - 1 ? (
                            <button type="button" onClick={next} className="btn-primary" data-testid="gs-next-btn">
                                Continue <ArrowRight size={16} weight="bold" />
                            </button>
                        ) : (
                            <button type="submit" disabled={loading} className="btn-primary" data-testid="gs-submit-btn">
                                {loading ? "Submitting…" : "Submit project"}
                                <PaperPlaneRight size={16} weight="bold" />
                            </button>
                        )}
                    </div>
                </form>

                <aside className="lg:col-span-2">
                    <div className="overline mb-3">Where we work</div>
                    <p className="text-slate-400 mb-5 text-sm">
                        Serving all 50 US states + Canadian provinces. Select your state in step 1 so we can match you
                        with a drafter familiar with local standards.
                    </p>
                    <NorthAmericaMap compact showLegend={false} />
                </aside>
            </div>
            </div>
        </div>
    );
}

function InputField({ label, type = "text", required, value, onChange, placeholder, testid }) {
    return (
        <div>
            <label className="overline mb-2 block">{label}{required && <span className="text-orange-400 ml-1">*</span>}</label>
            <input
                type={type}
                required={required}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                className="w-full bg-slate-900/70 border border-slate-700 rounded-lg px-3 py-2.5 text-slate-100 focus:outline-none focus:border-cyan-500"
                data-testid={testid}
            />
        </div>
    );
}

function SelectField({ label, required, value, onChange, options, testid }) {
    return (
        <div>
            <label className="overline mb-2 block">{label}{required && <span className="text-orange-400 ml-1">*</span>}</label>
            <select
                required={required}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="w-full bg-slate-900/70 border border-slate-700 rounded-lg px-3 py-2.5 text-slate-100 focus:outline-none focus:border-cyan-500"
                data-testid={testid}
            >
                {options.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                ))}
            </select>
        </div>
    );
}
